import React from 'react';
import {
  StackLayout,
  StackItem,
  HeadingText,
  ParagraphText,
  useClickableCard,
  Image,
  styled,
  type GDSHeadingLevel,
  type GDSPosition,
  Icon,
} from '@leagueplatform/genesis-core';
import {
  useMasonryEngineActionEmitter,
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { useUniqueId } from '@leagueplatform/web-common';
import { BodyTextHtmlTagOptions } from '#utils/body-text-html-tag-options.ts';
import {
  getIconToken,
  Indicator,
  getLabelByIndicator,
} from '#utils/icon-mapping.ts';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { ConditionalLinkCardRenderer } from '../common/conditional-link-card-renderer/conditional-link-card-renderer.component';
import { ConditionalLinkRenderer } from '../common/conditional-link-renderer/conditional-link-renderer.component';
import {
  MasonryDeeplinkAction,
  MasonryDriverAction,
  MasonryDismissAction,
} from '../../types/masonry-driver-node-actions';

export type MasonryExtraSmallCardNodeProperties = {
  heading: string;
  headingLevel?: GDSHeadingLevel;
  image?: string;
  body?: string;
  imageAltText?: string;
  imageAlignment?: GDSPosition;
  actionIndicator?: Indicator;
  actionIndicatorLabel?: string;
};

export type MasonryExtraSmallCardNodeActions = {
  onClick?: MasonryDriverAction;
  onDismiss?: MasonryDismissAction;
};

export type MasonryExtraSmallCardNode = MasonryEngineNode<
  'extraSmallCard',
  MasonryExtraSmallCardNodeProperties,
  MasonryExtraSmallCardNodeActions
>;

export type MasonryExtraSmallCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryExtraSmallCardNode>;

const StyledImage = styled(Image, {
  objectFit: 'cover',
  contentPosition: 'center',
  width: '42px',
  height: '42px',
});

export const MasonryExtraSmallCardRenderer = ({
  css,
  engineClassName,
  className,
  heading,
  headingLevel = '3',
  image,
  body,
  imageAltText = '',
  imageAlignment = 'leading',
  actionIndicator,
  actionIndicatorLabel,
  onClick,
  onDismiss,
  locatorId,
  rootElementRef,
}: MasonryExtraSmallCardRendererProps) => {
  const { primaryActionRef, clickAreaRef, onCardMouseDown, onCardMouseUp } =
    useClickableCard(rootElementRef);

  const isValidActionIcon = onClick && actionIndicator;

  const uniqueId = useUniqueId({ prefix: 'extraSmallCard' });
  const iconId = `${uniqueId}-icon`;
  const linkId = `${uniqueId}-link`;

  const invokeAction = useMasonryEngineActionEmitter();

  return (
    <ConditionalLinkCardRenderer
      hasLink={Boolean((onClick as MasonryDeeplinkAction)?.payload.url)}
      ref={clickAreaRef}
      onCardMouseUp={onCardMouseUp}
      onCardMouseDown={onCardMouseDown}
      onDismiss={onDismiss}
      data-testid={locatorId}
      className={formatClassNames(engineClassName, className)}
    >
      <StackLayout
        orientation="horizontal"
        verticalAlignment="center"
        spacing={image ? '$threeQuarters' : undefined}
        css={{ padding: '$one', ...css }}
      >
        {image && imageAlignment === 'leading' && (
          <StackItem verticalAlignment="center">
            <StyledImage src={image} alt={imageAltText} loading="lazy" />
          </StackItem>
        )}
        <StackLayout css={{ marginTop: '$quarter' }} verticalAlignment="center">
          {/* if there is no body text, the heading is unnecessary and should use 'display'  */}
          <HeadingText level={!body ? 'display' : headingLevel} size="sm">
            <ConditionalLinkRenderer
              ref={primaryActionRef}
              disableDefaultOnClick
              onClick={(event: React.MouseEvent) => {
                if (onClick) {
                  invokeAction({
                    event,
                    ...onClick,
                  });
                }
              }}
              {...{ id: linkId }}
              aria-labelledby={`${linkId} ${isValidActionIcon && iconId}`}
              url={(onClick as MasonryDeeplinkAction)?.payload.url}
            >
              {heading}
            </ConditionalLinkRenderer>
          </HeadingText>
          {body && (
            <ParagraphText size="sm" htmlStringOptions={BodyTextHtmlTagOptions}>
              {body}
            </ParagraphText>
          )}
        </StackLayout>
        {image && imageAlignment === 'trailing' && (
          <StackItem verticalAlignment="center">
            <StyledImage src={image} alt={imageAltText} loading="lazy" />
          </StackItem>
        )}
        {isValidActionIcon && (
          <StackItem verticalAlignment="center">
            <Icon
              icon={getIconToken(actionIndicator)}
              label={
                actionIndicatorLabel ?? getLabelByIndicator(actionIndicator)
              }
              id={iconId}
            />
          </StackItem>
        )}
      </StackLayout>
    </ConditionalLinkCardRenderer>
  );
};
